import React, { useEffect, useRef, useState } from "react";
import { api, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { Trash2, Plus, Upload, X, ImageOff, Loader2 } from "lucide-react";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const emptyForm = { name: "", slug: "", image_url: "", display_order: 0, active: true };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, display_order: cats.length + 1 });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ ...emptyForm, ...c });
    setOpen(true);
  };

  const closeDialog = (v) => {
    setOpen(v);
    if (!v) {
      setEditing(null);
      setForm(emptyForm);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, slug: form.slug || slugify(form.name), display_order: +form.display_order };
      if (editing) await api.put(`/admin/categories/${editing}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(editing ? "Category updated" : "Category created");
      closeDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold">Categories</h1>
        <Button
          data-testid="admin-category-add-btn"
          onClick={openNew}
          className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full"
        >
          <Plus className="w-4 h-4 mr-1" /> Add category
        </Button>
      </div>

      <div className="bg-hyd-panel border border-hyd-border rounded-xl overflow-hidden mt-5">
        <table className="w-full text-sm">
          <thead className="bg-hyd-panel2 text-hyd-text2 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-right p-3">Order</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hyd-border">
            {cats.map((c) => (
              <tr key={c.id} className="hover:bg-hyd-panel2/60">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-hyd-panel2 overflow-hidden grid place-items-center">
                      {c.image_url
                        ? <img src={imageUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover" />
                        : <ImageOff className="w-3.5 h-3.5 text-hyd-text3" />}
                    </div>
                    <span>{c.name}</span>
                  </div>
                </td>
                <td className="p-3 text-hyd-text2">{c.slug}</td>
                <td className="p-3 text-right">{c.display_order}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`category-edit-${c.slug}`} className="text-hyd-gold hover:bg-hyd-panel">Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(c)} className="text-hyd-red hover:bg-hyd-panel"><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit category popup */}
      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent className="bg-hyd-panel border-hyd-border text-hyd-text max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-3" data-testid="category-dialog-form">
            <F label="Name">
              <Input data-testid="category-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={() => !form.slug && set("slug", slugify(form.name))} required />
            </F>
            <F label="Slug">
              <Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} placeholder="auto" />
            </F>

            {/* Image: preview + file upload + delete */}
            <div>
              <Label className="text-xs text-hyd-text2 mb-1 block">Category image</Label>
              {form.image_url ? (
                <div className="relative w-full aspect-[3/2] rounded-2xl overflow-hidden border border-hyd-border bg-hyd-bg">
                  <img
                    src={imageUrl(form.image_url)}
                    alt="Category preview"
                    data-testid="category-image-preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      title="Upload new image"
                      data-testid="category-image-replace-btn"
                      className="h-8 w-8 grid place-items-center rounded-full bg-hyd-bg/85 text-hyd-gold hover:text-hyd-goldDim"
                    >
                      <Upload className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => set("image_url", "")}
                      title="Delete image"
                      data-testid="category-image-delete-btn"
                      className="h-8 w-8 grid place-items-center rounded-full bg-hyd-bg/85 text-hyd-red hover:bg-hyd-red hover:text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="category-image-upload-btn"
                  className="w-full aspect-[3/2] rounded-2xl border-2 border-dashed border-hyd-border grid place-items-center text-xs text-hyd-text2 hover:border-hyd-gold hover:text-hyd-gold transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-hyd-gold" />
                  ) : (
                    <div className="text-center">
                      <Upload className="w-5 h-5 mx-auto" />
                      <div className="mt-1">Upload image</div>
                    </div>
                  )}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" />
              {uploading && form.image_url && <div className="text-[10px] text-hyd-text3 mt-1">Uploading new image…</div>}
            </div>

            <F label="Display order">
              <Input type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} />
            </F>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.active} onCheckedChange={(v) => set("active", !!v)} /> Active
            </label>

            <div className="flex gap-2 pt-1">
              <Button data-testid={ADMIN.categorySave} type="submit" className="flex-1 bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full">
                {editing ? "Save changes" : (<><Plus className="w-4 h-4 mr-1" />Create category</>)}
              </Button>
              <Button type="button" variant="ghost" onClick={() => closeDialog(false)} className="text-hyd-text2">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-hyd-text2 mb-1 block">{label}</Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text">{children}</div>
    </div>
  );
}
